import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SERVICES = ["Artist Development", "A&R Strategy", "Data Analysis", "Marketing", "Audit Report", "General"];

const inputCls =
  "w-full bg-[#0C0C10] border border-[#22222B] px-5 py-4 text-sm text-[#F2F2F5] placeholder:text-[#60606E] focus:border-[#00F0FF] focus:outline-none transition-colors duration-300 mono";

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", service: "Artist Development", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    const onPrefill = (e) => {
      setForm((f) => ({
        ...f,
        service: e.detail.service,
        message: f.message || e.detail.message,
      }));
    };
    window.addEventListener("prefill-contact", onPrefill);
    return () => window.removeEventListener("prefill-contact", onPrefill);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Enquiry sent. We move fast — expect a reply soon.");
      setForm({ name: "", email: "", service: "Artist Development", message: "" });
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" && d.length < 140 ? d : "Send failed. Try again in a moment.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="relative py-28 sm:py-36 grid-bg" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-16">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="mono text-[11px] tracking-[0.35em] text-[#00F0FF] mb-4" data-testid="contact-eyebrow">
            BOOKING // OPEN CHANNEL
          </p>
          <h2 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95]" data-testid="contact-heading">
            READY TO GO FROM <span className="text-stroke-cyan">UNCHARTED</span> TO CHARTED?
          </h2>
          <p className="mt-8 text-sm sm:text-base text-[#A0A0AB] leading-relaxed max-w-md">
            Tell us who you are and where you want to be. We read everything ourselves —
            no interns, no bots. If the signal is real, we answer.
          </p>
          <div className="mt-10 space-y-3">
            <a
              href="https://instagram.com/plagiarhythm_llc"
              target="_blank"
              rel="noopener noreferrer"
              className="block mono text-[11px] tracking-[0.25em] text-[#A0A0AB] hover:text-[#CCFF00] transition-colors duration-300"
              data-testid="contact-instagram-link"
            >
              IG — @PLAGIARHYTHM_LLC
            </a>
            <a
              href="https://www.linkedin.com/in/aandrbeast"
              target="_blank"
              rel="noopener noreferrer"
              className="block mono text-[11px] tracking-[0.25em] text-[#A0A0AB] hover:text-[#CCFF00] transition-colors duration-300"
              data-testid="contact-linkedin-link"
            >
              LI — /IN/AANDRBEAST
            </a>
            <a
              href="mailto:ryan@plagiarhythm.com"
              className="block mono text-[11px] tracking-[0.25em] text-[#A0A0AB] hover:text-[#CCFF00] transition-colors duration-300"
              data-testid="contact-email-link"
            >
              EMAIL — RYAN@PLAGIARHYTHM.COM
            </a>
            <a
              href="tel:+16195869291"
              className="block mono text-[11px] tracking-[0.25em] text-[#A0A0AB] hover:text-[#CCFF00] transition-colors duration-300"
              data-testid="contact-phone-link"
            >
              CALL — (619) 586-9291
            </a>
          </div>
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="space-y-5 tech-corner p-6 sm:p-8 bg-[#08080A]/60 border border-[#22222B]"
          data-testid="contact-form"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <input
              required
              name="name"
              value={form.name}
              onChange={set("name")}
              placeholder="YOUR NAME"
              className={inputCls}
              data-testid="contact-name-input"
            />
            <input
              required
              type="email"
              name="email"
              value={form.email}
              onChange={set("email")}
              placeholder="YOUR EMAIL"
              className={inputCls}
              data-testid="contact-email-input"
            />
          </div>
          <select
            name="service"
            value={form.service}
            onChange={set("service")}
            className={`${inputCls} appearance-none cursor-pointer`}
            data-testid="contact-service-select"
          >
            {SERVICES.map((s) => (
              <option key={s} value={s} className="bg-[#0C0C10]">
                {s.toUpperCase()}
              </option>
            ))}
          </select>
          <textarea
            required
            name="message"
            rows={5}
            value={form.message}
            onChange={set("message")}
            placeholder="YOUR SOUND, YOUR GOALS, YOUR TIMELINE..."
            className={`${inputCls} resize-none`}
            data-testid="contact-message-input"
          />
          <button
            type="submit"
            disabled={sending}
            className="group w-full mono text-xs tracking-[0.25em] font-semibold px-7 py-5 bg-[#00F0FF] text-black hover:bg-[#CCFF00] disabled:opacity-60 transition-colors duration-300 flex items-center justify-center gap-2"
            data-testid="contact-submit-button"
          >
            {sending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> TRANSMITTING...
              </>
            ) : (
              <>
                SEND THE SIGNAL
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </>
            )}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
